import { useCallback, useMemo, useState, type ReactElement } from 'react'
import { AppDialog, DateRangePicker, Textarea, type AppDialogAction, type DateRangeValue } from '../Common'
import { requestEduLeave } from '../../services/eduLeaveService'
import { mapEduServerError } from '../Education/classesBuilderHelpers'
import { useNls } from '../../nls/nlsProvider'
import './familyLeave.css'

export interface FamilyLeaveDialogProps {
  open: boolean
  workspaceId: string
  learnerId: string
  initialRange?: { start?: string; end?: string }
  onClose: () => void
  onSuccess: () => void
}

const computeRangeDays = (start: string | null | undefined, end: string | null | undefined): number => {
  if (!start || !end) return 0
  const [sy, sm, sd] = start.split('-').map(Number)
  const [ey, em, ed] = end.split('-').map(Number)
  if (isNaN(sy) || isNaN(sm) || isNaN(sd) || isNaN(ey) || isNaN(em) || isNaN(ed)) return 0
  const startMs = Date.UTC(sy, sm - 1, sd)
  const endMs = Date.UTC(ey, em - 1, ed)
  if (endMs < startMs) return 0
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1
}

const computeDeviceMinDate = (): string => {
  const d = new Date()
  d.setDate(d.getDate() - 7)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export const FamilyLeaveDialog = ({
  open,
  workspaceId,
  learnerId,
  initialRange,
  onClose,
  onSuccess,
}: FamilyLeaveDialogProps): ReactElement | null => {
  const { t } = useNls()

  const [range, setRange] = useState<DateRangeValue>({
    start: initialRange?.start ?? '',
    end: initialRange?.end ?? '',
  })
  const [reason, setReason] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const minDate = useMemo(() => computeDeviceMinDate(), [])
  const rangeDays = useMemo(() => computeRangeDays(range.start, range.end), [range.start, range.end])
  const isRangeTooLong = rangeDays > 31

  const trimmedReason = reason.trim()
  const isReasonValid = trimmedReason.length >= 3 && trimmedReason.length <= 500
  const isRangeValid = Boolean(range.start && range.end && !isRangeTooLong)
  const isSubmitDisabled = !isRangeValid || !isReasonValid || isSubmitting

  const handleReset = useCallback(() => {
    setRange({
      start: initialRange?.start ?? '',
      end: initialRange?.end ?? '',
    })
    setReason('')
    setError(null)
    setIsSubmitting(false)
  }, [initialRange?.start, initialRange?.end])

  const handleCancel = useCallback(() => {
    if (isSubmitting) return
    handleReset()
    onClose()
  }, [isSubmitting, handleReset, onClose])

  const handleSubmit = async () => {
    if (isSubmitDisabled) return

    setIsSubmitting(true)
    setError(null)

    try {
      await requestEduLeave({
        workspaceId,
        learnerId,
        fromDate: range.start,
        toDate: range.end,
        reason: trimmedReason,
      })
      handleReset()
      onSuccess()
      onClose()
    } catch (err: unknown) {
      setError(mapEduServerError(err, t))
      setIsSubmitting(false)
    }
  }

  const actions: AppDialogAction[] = [
    {
      label: t('NLSFamilyLeaveDialogCancel'),
      variant: 'ghost',
      onClick: handleCancel,
      disabled: isSubmitting,
    },
    {
      label: isSubmitting
        ? t('NLSFamilyLeaveDialogSubmitting')
        : t('NLSFamilyLeaveDialogSubmit'),
      variant: 'primary',
      onClick: handleSubmit,
      disabled: isSubmitDisabled,
      loading: isSubmitting,
    },
  ]

  if (!open) return null

  return (
    <AppDialog
      open={open}
      title={t('NLSFamilyLeaveDialogTitle')}
      onDismiss={isSubmitting ? undefined : handleCancel}
      actions={actions}
      size="md"
    >
      <div className="familyLeaveDialog">
        {error && <div className="familyLeaveDialog__error">{error}</div>}

        <div className="familyLeaveDialog__field">
          <label className="familyLeaveDialog__label">
            {t('NLSFamilyLeaveDialogDatesLabel')}
          </label>
          <DateRangePicker
            startValue={range.start}
            endValue={range.end}
            min={minDate}
            onChange={setRange}
            placeholder={t('NLSFamilyLeaveDialogDatesPlaceholder')}
            disabled={isSubmitting}
          />
          {isRangeTooLong && (
            <div className="familyLeaveDialog__hint">
              {t('NLSFamilyLeaveDialogRangeMaxHint')}
            </div>
          )}
        </div>

        <div className="familyLeaveDialog__field">
          <label className="familyLeaveDialog__label">
            {t('NLSFamilyLeaveDialogReasonLabel')}
          </label>
          <Textarea
            value={reason}
            onChange={setReason}
            maxLength={500}
            disabled={isSubmitting}
            placeholder={t('NLSFamilyLeaveDialogReasonPlaceholder')}
          />
        </div>
      </div>
    </AppDialog>
  )
}
